import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MessageCircle, Send, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminWhatsApp() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp/pending`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (messageId) => {
    setSending(prev => ({ ...prev, [messageId]: true }));
    try {
      const response = await axios.post(`${API}/admin/whatsapp/resend/${messageId}`);
      if (response.data.success) {
        toast.success('✅ Mensaje enviado exitosamente');
        fetchMessages();
      } else {
        toast.error(`❌ ${response.data.error || 'Error al enviar'}`);
      }
    } catch (error) {
      toast.error('Error al reenviar mensaje');
    } finally {
      setSending(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const handleRetryAllPending = async () => {
    const pendingMessages = messages.filter(m => m.status === 'pending');
    if (pendingMessages.length === 0) {
      toast.success('No hay mensajes pendientes');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/whatsapp/retry-pending`);
      const { sent, failed } = response.data;
      
      if (sent > 0) {
        toast.success(`✅ ${sent} mensaje(s) enviado(s)`);
      }
      if (failed > 0) {
        toast.error(`❌ ${failed} mensaje(s) fallaron (regla 24hrs)`);
      }
      fetchMessages();
    } catch (error) {
      toast.error('Error al reenviar mensajes');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            Enviado
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
            <Clock className="w-3 h-3 mr-1" />
            Pendiente
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            Fallido
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = messages.filter(m => m.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando mensajes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-green-600" />
              Mensajes WhatsApp
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchMessages}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
            {pendingCount > 0 && (
              <Button 
                onClick={handleRetryAllPending}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="h-4 w-4 mr-2" />
                Enviar {pendingCount} pendiente(s)
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-800">{messages.length}</div>
            <div className="text-sm text-slate-500">Total (24 hrs)</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{messages.filter(m => m.status === 'sent').length}</div>
            <div className="text-sm text-slate-500">Enviados</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
            <div className="text-sm text-slate-500">Pendientes</div>
          </Card>
        </div>

        {/* Messages List */}
        {messages.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No hay mensajes en las últimas 24 horas</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <Card key={msg.id} className="p-4">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-800">{msg.provider_name}</span>
                      {getStatusBadge(msg.status)}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p>📱 {msg.provider_phone}</p>
                      <p>💰 Q{msg.payment_amount?.toLocaleString('es-GT', { minimumFractionDigits: 2 })}</p>
                      <p>🏪 {msg.store_name}</p>
                      {msg.created_at && (
                        <p className="text-xs text-slate-400">
                          Creado: {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </p>
                      )}
                      {msg.sent_at && (
                        <p className="text-xs text-green-600">
                          Enviado: {format(new Date(msg.sent_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </p>
                      )}
                      {msg.error_message && (
                        <p className="text-xs text-red-500">Error: {msg.error_message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResend(msg.id)}
                      disabled={sending[msg.id]}
                      className="border-green-500 text-green-600 hover:bg-green-50"
                    >
                      {sending[msg.id] ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      {msg.status === 'sent' ? 'Reenviar' : 'Enviar'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
